import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Chip,
  DialogContent,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import StandardDialog from "../../components/StandardDialog.js";
import { experimental_VGrid as VGrid } from "virtua";
import SearchBar from "../../components/SearchBar.js";
import AddOrEmptyTile from "../../components/AddOrEmptyTile.js";
import PosterTile from "../../components/PosterTile.js";
import SortableItem from "../../components/SortableItem.js";
import useApi from "../../hooks/useApi.js";
import RefreshIcon from "@mui/icons-material/Refresh";
import RemoveIcon from "@mui/icons-material/Remove";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import CollectionMetaDataTile from "./Metadata.js";
import CollectionSkeleton from "./CollectionSkeleton.js";
import { motion } from "framer-motion";
import { useDetailNavigation } from "../../hooks/useDetailNavigation.js";
import { useScaleGestures } from "../../hooks/useScaleGestures.js";
import { useDebounce } from "../../hooks/useDebounce.js";
import { useAuth } from "../../context/authContext.js";
import { getContentLabel, getContentOptionsArray } from "../../utils/utils.js";
import useResizeObserver from "../../hooks/useResizeObserver.js";
import { useListMutations } from "../../hooks/useCustomQuery.js";
import { getProfileKind } from "../../utils/listSchema.js";
import { useThemeContext } from "../../context/themeContext.js";
import { useMutation } from "@tanstack/react-query";
import CollectionTrackingHelper from "./CollectionTrackingHelper.js";

const HELPER_KEY = "show-status-tracker-collections-helper-mobile";

const Collection = ({
  list,
  backgroundColor,
  onDelete,
  display = "modal",
  onClose,
  onPendingInvalidation,
  fullScreen,
  readOnly,
  enableTrackingHelper = false,
  showType = false,
}) => {
  const metadataRef = useRef(null);
  const { height: metadataHeight } = useResizeObserver(metadataRef);
  const { fetchSeasons, editUiState } = useApi();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const { isTouchDevice } = useThemeContext();
  const { user, updateUser } = useAuth();
  const isAdmin = Boolean(user?.isAdmin);
  // Admins get full item-level actions (reorder/replace/remove) on otherwise
  // read-only collections, but only on desktop — the action panel's hover/tap
  // model conflicts with the tap-to-navigate gesture on touch devices. Scoped to
  // canonical lists because readOnly also covers other users' and saved
  // collections, which admins don't own and the backend would reject.
  const canEditItems =
    !readOnly || (isAdmin && !isTouchDevice && list.origin === "canonical");
  const [helperDismissed, setHelperDismissed] = useState(false);

  const dismissHelperMutation = useMutation({
    mutationFn: editUiState,
    onSuccess: () => {
      // Patch the in-memory user so any subsequent Collection mount reads the
      // dismissed state without waiting for a full re-auth.
      updateUser({
        ...user,
        uiState: { ...user.uiState, [HELPER_KEY]: false },
      });
    },
  });

  // Show on touch devices only for opt-in surfaces. Signed-out users always see it;
  // signed-in users see it until they dismiss (persisted via ui_state).
  const showTrackingHelper =
    !helperDismissed &&
    isTouchDevice &&
    enableTrackingHelper &&
    (!user || user.uiState?.[HELPER_KEY] !== false);

  const handleHelperClose = () => {
    setHelperDismissed(true);
    if (user) {
      dismissHelperMutation.mutate({ key: HELPER_KEY, value: false });
    }
  };
  const { list_id, size, content_type } = list;
  const [openSearchDialog, setOpenSearchDialog] = useState(false);
  const [itemToReplace, setItemToReplace] = useState(null);
  const [action, setAction] = useState("");
  // For mixed collections: the content type the user picks before the search bar appears.
  // Null until chosen (add flow), or pre-set from the replaced item's type (replace flow).
  const [searchContentType, setSearchContentType] = useState(null);
  const [draggableItem, setDraggableItem] = useState(null);
  const { openContent } = useDetailNavigation();
  // Every tile shows the status overlay, so has PosterTile's long press — hover
  // only, as a tap press-in would mute its pop. Inside SortableItem so it never
  // shares an element with dnd-kit's drag transform.
  const hoverGestures = useScaleGestures({ tap: false });
  const { addToList, removeFromList, replaceInList, reorderListAsync } =
    useListMutations({
      origin: list.origin,
      profileKind: getProfileKind(list),
      username: user?.username,
    });
  const [editedList, setEditedList] = useState(list);
  const numberOfItems = size ? size : list.items.length ? list.items.length : 1;
  const [seriesInfo, setSeriesInfo] = useState({ title: "", seasons: [] });
  const containerRef = useRef(null);
  const { width: containerWidth } = useResizeObserver(containerRef);
  const NUM_COLUMNS = fullScreen ? 3 : isMdUp ? 6 : 4;
  const gutterSize = 12; // MUI spacing of 2
  // The VGrid is a scroll container, so it clips anything past its edges — and
  // the edge tiles sit flush against them. This padding gives their hover scale
  // and long-press pop room to grow; a matching negative margin cancels it out,
  // so the content box (and every size computed from containerWidth) is unchanged.
  const scaleCushion = 4;
  // Reserve one gutter *between* each pair of columns — (NUM_COLUMNS - 1) total,
  // not one per column — so no gutter is wasted on the row's outer edges. Tiles
  // fill the full width and render a touch larger as a result.
  const tileWidth = containerWidth
    ? Math.floor(
        (containerWidth - gutterSize * (NUM_COLUMNS - 1)) / NUM_COLUMNS,
      )
    : 185;
  const tileHeight = tileWidth * 1.5027;

  // Fullscreen VGrid height must be a definite length — virtua can't virtualise against a
  // percentage that resolves to auto (the container isn't a flex-grow child), so we size off
  // the viewport. Subtract, from 100dvh: the safe-area top/bottom insets (0px outside the
  // installed-PWA/notched case, where 100dvh otherwise includes them), the dialog's top+bottom
  // inset (32px — StandardDialog insets the paper by m:2 on mobile), the outer box padding
  // (32px, p:2 top+bottom), the gap between metadata and grid (16px), and the measured
  // metadata height.
  const vGridHeight = fullScreen
    ? `calc(100dvh - var(--safe-top) - var(--safe-bottom) - ${metadataHeight + 16 + 32 + 32}px)`
    : numberOfItems < 7
      ? (containerWidth / NUM_COLUMNS) * 1.5
      : 500;

  useEffect(() => {
    // Update state to use the latest data whenever list changes
    setEditedList(list);
  }, [list, numberOfItems, display]);

  const updateEditedList = (name, value) => {
    setEditedList((prevList) => ({
      ...prevList,
      [name]: typeof value === "function" ? value(prevList[name]) : value,
    }));
  };

  const handleOpenSearchDialog = (item, action) => {
    setOpenSearchDialog(true);
    setItemToReplace(item);
    setAction(action);
  };

  const handleCloseSearchDialog = () => {
    setOpenSearchDialog(false);
    setItemToReplace(null);
    setAction("");
    setSeriesInfo({ title: "", seasons: [] });
    setSearchContentType(null);
  };

  // For non-mixed lists this is just content_type. For mixed "replace" we use the
  // replaced item's type directly (no picker needed). For mixed "add" it starts null
  // and is set when the user picks a type from the content type picker.
  const effectiveSearchType =
    content_type === "mixed"
      ? action === "replace"
        ? (itemToReplace?.content_type ?? null)
        : searchContentType
      : content_type;

  const debouncedReorderListMutation = useDebounce(async (newItems) => {
    await reorderListAsync({ listId: list_id, items: newItems });
  }, 3000); // Debounce with a 3-second delay

  useEffect(() => {
    return () => {
      // Execute pending changes on dismount if collection is closed before debounce delay
      debouncedReorderListMutation.flush();
    };
  }, [debouncedReorderListMutation]);

  const handleSearchbarSelect = async (content_id, content_type) => {
    if (content_type === "tvshow") {
      const { series_title, seasons } = await fetchSeasons(content_id);
      setSeriesInfo({ title: series_title, seasons });
    } else {
      action === "replace" ? handleReplace(content_id) : handleAdd(content_id);
    }
  };

  const handleAdd = useCallback(
    (contentId) => {
      addToList({
        listId: list_id,
        contentId,
      });
      // handleCloseSearchDialog(); // currently disabled to ensure searchbar remains open if users want to add more than one title to collection
    },
    [addToList, list_id],
  );

  const handleReplace = useCallback(
    async (newContentId) => {
      await debouncedReorderListMutation.flush();
      replaceInList({
        listId: list_id,
        oldContentId: itemToReplace.content_id,
        newContentId,
      });
      handleCloseSearchDialog();
    },
    [debouncedReorderListMutation, itemToReplace, list_id, replaceInList],
  );

  const handleRemove = async (contentId) => {
    await debouncedReorderListMutation.flush(); // ensure any pending reorders are applied before
    await removeFromList({
      listId: list_id,
      contentId,
    });
  };

  useEffect(() => {
    if (
      seriesInfo.seasons.length === 1 &&
      seriesInfo.seasons[0].content_id &&
      seriesInfo.title
    ) {
      const contentId = seriesInfo.seasons[0].content_id;
      if (action === "replace") {
        handleReplace(contentId);
      } else {
        handleAdd(contentId);
      }
      setSeriesInfo({ title: "", seasons: [] });
    }
  }, [seriesInfo, action, handleAdd, handleReplace]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragStart(event) {
    const { active } = event;
    const matchedItem = editedList.items.find(
      (item) => item.content_id === active.id,
    );
    setDraggableItem(matchedItem);
  }

  function handleDragEnd(event) {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setDraggableItem(null);
      updateEditedList("items", (currentItems) => {
        const oldIndex = currentItems.findIndex(
          (item) => item.content_id === active.id,
        );
        const newIndex = currentItems.findIndex(
          (item) => item.content_id === over.id,
        );

        const newItems = arrayMove(currentItems, oldIndex, newIndex).map(
          (item, index) => ({
            ...item,
            position: index + 1,
          }),
        );

        const reorderPayload = newItems.map(({ content_id, position }) => ({
          content_id,
          position,
        }));

        debouncedReorderListMutation(reorderPayload); // ensures API call is only made once, x seconds after the last reorder
        return newItems;
      });
    }
  }

  let AddOrEmptyTilePlaceholders = [];

  if (size) {
    const remainingSlots = Math.max(size - editedList.items.length, 0);
    AddOrEmptyTilePlaceholders = Array.from({ length: remainingSlots }, () => ({
      _isAddOrEmptyTile: true,
    }));
  } else if (editedList.items.length === 0) {
    // No size limit, list is empty — show one AddOrEmptyTile
    AddOrEmptyTilePlaceholders = [{ _isAddOrEmptyTile: true }];
  }

  const itemsForGrid = [
    ...(showTrackingHelper ? [{ _isTrackingHelper: true }] : []),
    ...editedList.items,
    ...AddOrEmptyTilePlaceholders,
  ];
  const totalRows = Math.ceil(itemsForGrid.length / NUM_COLUMNS);

  return (
    <>
      <Box
        sx={{
          p: 2,
          width: "100%",
          // Fill the dialog paper so its background covers the full inset area. The
          // paper is given a definite height by the caller (Curate.js), so 100% here
          // resolves correctly whether the paper is edge-to-edge or inset on mobile.
          height: fullScreen ? "100%" : undefined,
          minHeight: numberOfItems === 1 && !fullScreen ? "334.5px" : undefined,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          gap: 2,
          overflow: "hidden",
        }}
      >
        <Box ref={metadataRef}>
          <CollectionMetaDataTile
            editedList={editedList}
            updateEditedList={updateEditedList}
            setEditedList={setEditedList}
            originalList={list}
            onDelete={onDelete}
            onAdd={() => handleOpenSearchDialog(null, "add")}
            isInline={false}
            onCollectionClose={onClose}
            onPendingInvalidation={onPendingInvalidation}
            fullScreen={fullScreen}
            showClose={true}
            readOnly={readOnly}
          />
        </Box>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={editedList.items.map((item) => item.content_id)}
            strategy={rectSortingStrategy}
          >
            <Box ref={containerRef} width="100%">
              {containerWidth > 0 && metadataHeight > 0 ? (
                <VGrid
                  key={containerWidth}
                  style={{
                    // Grow by the cushion on each side, so after padding the
                    // content box is the same size it was without it
                    height: `calc(${
                      typeof vGridHeight === "number"
                        ? `${vGridHeight}px`
                        : vGridHeight
                    } + ${scaleCushion * 2}px)`,
                    width: `calc(100% + ${scaleCushion * 2}px)`,
                    padding: scaleCushion,
                    margin: -scaleCushion,
                  }}
                  row={totalRows}
                  col={NUM_COLUMNS}
                  // Width hint = the narrowest column (the last, which has no
                  // trailing gutter). Wider columns measure themselves from their
                  // own content box, so virtua lands on the right per-column widths.
                  cellWidth={tileWidth}
                  cellHeight={tileHeight + gutterSize}
                >
                  {({ rowIndex, colIndex }) => {
                    const index = rowIndex * NUM_COLUMNS + colIndex;
                    const item = itemsForGrid[index];
                    if (!item) return null;

                    // Build the cell's content first; the wrapper below handles
                    // sizing and the inter-column gutter uniformly for every type.
                    let content;
                    if (item._isTrackingHelper) {
                      content = (
                        <CollectionTrackingHelper onClose={handleHelperClose} />
                      );
                    } else if (item._isAddOrEmptyTile) {
                      content = (
                        <AddOrEmptyTile
                          onClick={() => handleOpenSearchDialog(null, "add")}
                          readOnly={readOnly}
                        />
                      );
                    } else {
                      content = (
                        <SortableItem
                          key={item.content_id}
                          id={item.content_id}
                        >
                          {({ attributes, listeners }) => (
                            <motion.div {...hoverGestures}>
                              <PosterTile
                                contentId={item.content_id}
                                contentType={item.content_type}
                                contentTitle={item.title}
                                imageUrl={item.image_url}
                                onClick={() =>
                                  openContent({
                                    contentId: item.content_id,
                                    contentType: item.content_type,
                                    title: item.title,
                                  })
                                }
                                showStatus="full"
                                position={
                                  editedList.is_ranked ? item.position : null
                                }
                                showType={showType}
                                listActions={
                                  canEditItems
                                    ? [
                                        ...(editedList.items.length > 1
                                          ? [
                                              {
                                                label: "Reorder",
                                                value: "reorder",
                                                icon: (
                                                  <DragIndicatorIcon
                                                    {...attributes}
                                                    {...listeners}
                                                    sx={{
                                                      touchAction: "none",
                                                      cursor: "grab",
                                                    }}
                                                    fontSize="inherit"
                                                  />
                                                ),
                                              },
                                            ]
                                          : []),
                                        {
                                          label: "Replace",
                                          value: "replace",
                                          icon: (
                                            <RefreshIcon fontSize="inherit" />
                                          ),
                                          onClick: () => {
                                            handleOpenSearchDialog(
                                              item,
                                              "replace",
                                            );
                                          },
                                        },
                                        {
                                          label: "Remove",
                                          value: "remove",
                                          icon: (
                                            <RemoveIcon fontSize="inherit" />
                                          ),
                                          onClick: () => {
                                            handleRemove(item.content_id);
                                          },
                                        },
                                      ]
                                    : []
                                }
                              />
                            </motion.div>
                          )}
                        </SortableItem>
                      );
                    }

                    // The gutter is trailing space carried inside the cell, so the
                    // last column omits it — tiles then run edge to edge with no
                    // dead space at the row's sides. The inner box holds the tile
                    // at its true width; the outer box's extra width is what virtua
                    // measures as this column's width.
                    const isLastCol = colIndex === NUM_COLUMNS - 1;
                    return (
                      <Box
                        sx={{
                          width: tileWidth + (isLastCol ? 0 : gutterSize),
                          height: tileHeight,
                        }}
                      >
                        <Box sx={{ width: tileWidth, height: tileHeight }}>
                          {content}
                        </Box>
                      </Box>
                    );
                  }}
                </VGrid>
              ) : (
                // Brief gap while the container measures itself before the VGrid
                // can mount — fill it with a skeleton so the grid area never flashes
                // empty. Header is omitted as the real metadata is already shown above.
                <CollectionSkeleton
                  fullScreen={fullScreen}
                  padding={0}
                  showHeader={false}
                />
              )}
            </Box>
          </SortableContext>

          <DragOverlay>
            {draggableItem ? (
              <PosterTile
                contentId={draggableItem.content_id}
                contentType={draggableItem.content_type}
                contentTitle={draggableItem.title}
                imageUrl={draggableItem.image_url}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </Box>

      <StandardDialog
        open={openSearchDialog}
        onClose={handleCloseSearchDialog}
        maxWidth="sm"
        topOffset="8vh"
        disableRestoreFocus={process.env.NODE_ENV === "development"}
      >
        <DialogContent
          sx={{
            border: "none",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            minHeight: 108,
            p: 2,
          }}
        >
          {seriesInfo.seasons.length > 1 ? (
            <Box>
              <Typography variant="body2">
                Select which season of <b>{seriesInfo.title}</b> to add...
              </Typography>
              <Tabs
                value={false} // uncontrolled
                variant="scrollable"
                scrollButtons="auto"
                onChange={(_, contentId) => {
                  action === "replace"
                    ? handleReplace(contentId)
                    : handleAdd(contentId);
                  setSeriesInfo({ title: "", seasons: [] });
                }}
                sx={{
                  maxWidth: "100%",
                  minHeight: 30,
                  mt: 2,
                }}
              >
                {seriesInfo.seasons.map(({ season_number, content_id }) => (
                  <Tab
                    key={content_id}
                    value={content_id}
                    label={
                      season_number === 0 ? "Whole series" : `S${season_number}`
                    }
                    sx={{
                      minWidth: 20,
                      py: 0,
                      minHeight: 30,
                    }}
                  />
                ))}
              </Tabs>
            </Box>
          ) : effectiveSearchType === null ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Select content type to add
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {getContentOptionsArray().map(
                  ({ value, labelSingular, icon }) => (
                    <Chip
                      key={value}
                      icon={React.cloneElement(icon, { fontSize: "small" })}
                      label={labelSingular}
                      onClick={() => setSearchContentType(value)}
                      variant="outlined"
                      clickable
                      size="small"
                      sx={{ "& .MuiChip-icon": { marginLeft: "8px" } }}
                    />
                  ),
                )}
              </Stack>
            </Box>
          ) : (
            <SearchBar
              contentType={effectiveSearchType}
              onSelect={handleSearchbarSelect}
              onClose={handleCloseSearchDialog}
              fullscreen={fullScreen}
              enableQuickAdd={false}
              clearOnSelect={true}
              title={
                action === "replace" ? (
                  <>
                    Replace <strong>{itemToReplace.title}</strong> with...
                  </>
                ) : (
                  <>
                    Add{" "}
                    {getContentLabel({
                      type: effectiveSearchType,
                      lowercase: true,
                    })}{" "}
                    to <strong>{list.title}</strong>
                  </>
                )
              }
            />
          )}
        </DialogContent>
      </StandardDialog>
    </>
  );
};

export default Collection;
